import { DOCUMENTATION_CHANGED_MEANWHILE_MESSAGE_ID } from 'shared/utils/errorHandling/constants';
import * as dbDocumentationsService from '../db/documentations/documentationsService';
import * as dbDocumentations from '../db/documentations/documentations';
import * as dbIssues from '../db/issues/issues';
import * as dbLevels from '../db/levels/levels';
import * as dbSites from '../db/sites/sites';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { debugLog } from 'shared/logger/debugLog';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { documentationEntityToModel } from 'serviceWorker/repository/documentation/mappings';
import { getUsersSynchronized } from 'serviceWorker/services/users/getUsers';
import { awaitEntities } from 'serviceWorker/services/factories/awaitEntities';
import { waitForMessage } from 'serviceWorker/services/factories/waitForMessage';
import { createDocumentationIssuesMap } from 'serviceWorker/repository/documentation/createDocumentationIssuesMap';
import { createIssueNumberMap } from 'serviceWorker/repository/documentation/createIssueNumberMap';
import { createDocumentationSitesAndLevelsMap } from 'serviceWorker/repository/documentation/createDocumentationSitesAndLevelsMap';
import { getDocumentationsByQuery, getSearchedDocumentations } from './documentationsData';
import { documentationFilteringCacheManager } from 'serviceWorker/repository/documentation/documentationFilteringCacheManager/index';
import {
  documentationContract,
  type UploadedDocumentationPayload,
} from 'shared/domain/documentation/documentationContract';
import { directoryContract } from 'shared/domain/directory/directoryContract';
import { makeErrorPublisher, makeEventPublisher } from 'serviceWorker/broadcasts/publishers';

export const broadcastDocumentationError = makeErrorPublisher(
  documentationContract
);

/** One documentation by its remote id, with its issues, sites and levels. */
export async function getDocumentationModel(id: string): Promise<DocumentationModel> {
  const [documentation, users, issues, levels, sites] = await Promise.all([
    dbDocumentations.getOneByRemote(id),
    getUsersSynchronized(),
    dbIssues.getAll(),
    dbLevels.get(),
    dbSites.get(),
  ]);
  if (!documentation) {
    throw new Error(`Cannot find documentation ${id}`);
  }
  const docIssuesMap = createDocumentationIssuesMap(issues, levels);
  const issueNumbers = createIssueNumberMap(issues);
  const { docSitesMap, docLevelsMap } =
    createDocumentationSitesAndLevelsMap(sites, levels);

  return documentationEntityToModel(
    documentation,
    users,
    docIssuesMap,
    issueNumbers,
    docSitesMap,
    docLevelsMap
  );
}

const emitAllDocumentations = makeEventPublisher(documentationContract, DomainMessagesTypes.allDocumentations);

export const broadcastHasAllDocumentations = (): void => {
  emitAllDocumentations({ hasAll: true });
};

const emitCreatedDocumentation = makeEventPublisher(documentationContract, DomainMessagesTypes.createdEntity);

const emitUpdatedDocumentation = makeEventPublisher(documentationContract, DomainMessagesTypes.updatedEntity);

const emitUploadedDocumentation = makeEventPublisher(documentationContract, DomainMessagesTypes.uploadedEntity);

export const broadcastFailUploadedDocumentation =
  makeEventPublisher(documentationContract, DomainMessagesTypes.failUploadedEntity);

const emitDocumentationMoved = makeEventPublisher(documentationContract, DomainMessagesTypes.documentationMoved);

const emitDocumentationDeleted = makeEventPublisher(documentationContract, DomainMessagesTypes.documentationDeleted);

export function broadcastCreatedDocumentation(
  payload: number[],
  uniqueId?: string
): void {
  invalidateCachesForPayload(payload);
  emitCreatedDocumentation(payload, uniqueId);
}

export function broadcastUpdatedDocumentation(
  payload: unknown,
  uniqueId?: string
): void {
  invalidateCachesForPayload(payload);
  emitUpdatedDocumentation(payload, uniqueId);
}

export function broadcastUploadedDocumentation(
  payload: UploadedDocumentationPayload,
  uniqueId?: string
): void {
  invalidateCachesForPayload(payload);
  emitUploadedDocumentation(payload, uniqueId);
}

export function broadcastDocumentationMoved(
  payload: unknown,
  uniqueId?: string
): void {
  invalidateCachesForPayload(payload);
  emitDocumentationMoved(payload, uniqueId);
}

export function broadcastDocumentationDeleted(
  payload: unknown,
  uniqueId?: string
): void {
  invalidateCachesForPayload(payload);
  emitDocumentationDeleted(payload, uniqueId);
}

function invalidateCachesForPayload(payload: unknown): void {
  const parentIds = extractParentIds(payload);

  if (parentIds.length > 0) {
    documentationFilteringCacheManager.invalidateDocumentationFilteringCachesByParentIds(
      parentIds
    );
    return;
  }

  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
}

function extractParentIds(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const source = payload as Record<string, unknown>;
  const knownParentIdFields = [
    'parentId',
    'oldParentId',
    'newParentId',
    'sourceParentId',
    'targetParentId',
  ];

  const collectedParentIds = new Set<string>();

  knownParentIdFields.forEach((fieldName) => {
    const fieldValue = source[fieldName];
    if (typeof fieldValue === 'string' && fieldValue.length > 0) {
      collectedParentIds.add(fieldValue);
    }
  });

  const explicitParentIds = source.parentIds;
  if (Array.isArray(explicitParentIds)) {
    explicitParentIds.forEach((parentId) => {
      if (typeof parentId === 'string' && parentId.length > 0) {
        collectedParentIds.add(parentId);
      }
    });
  }

  return Array.from(collectedParentIds);
}

export const broadcastCreatedDocumentationError = (error: any): void => {
  debugLog('broadcastCreatedDocumentationError', error);
  broadcastDocumentationError(DomainMessagesTypes.createdEntity, error);
};

export const broadcastUpdatedDocumentationError = (error: any): void => {
  debugLog('broadcastUpdatedDocumentationError', error);
  broadcastDocumentationError(DomainMessagesTypes.updatedEntity, error);
};

/** A documentation change was dropped: someone else changed the documentation meanwhile. */
export function broadcastDocumentationChangedMeanwhile(error: unknown): void {
  broadcastDocumentationError(DomainMessagesTypes.updatedEntity, error, {
    messageId: DOCUMENTATION_CHANGED_MEANWHILE_MESSAGE_ID,
  });
}

export const broadcastDocumentationMovedError = (error: any): void => {
  debugLog('broadcastCreatedDocumentationError', error);
  broadcastDocumentationBatchError(
    DomainMessagesTypes.documentationMoved,
    error
  );
};

export const broadcastDocumentationDeletedError = (error: any): void => {
  debugLog('broadcastDocumentationDeletedError', error);
  broadcastDocumentationBatchError(
    DomainMessagesTypes.documentationDeleted,
    error
  );
};

const publishDocumentationError = makeErrorPublisher(documentationContract);
const publishDirectoryError = makeErrorPublisher(directoryContract);

/** A batch spans files and folders: both views hear of its failure. */
function broadcastDocumentationBatchError(
  type: DomainMessagesTypes.documentationMoved | DomainMessagesTypes.documentationDeleted,
  error: unknown
): void {
  publishDocumentationError(type, error);
  publishDirectoryError(type, error);
}

function awaitDocumentationsDownload(): Promise<void> {
  return awaitEntities(
    dbDocumentationsService,
    waitForMessage(
      documentationContract,
      DomainMessagesTypes.allDocumentations
    )
  );
}

/** Files directly in `parentId`, once documentations are downloaded. */
export async function getDocumentationChildren(
  parentId: string
): Promise<DocumentationModel[]> {
  await awaitDocumentationsDownload();
  return getDocumentationsByQuery(parentId);
}

/** Files matching `searchPhrase`, once documentations are downloaded. */
export async function searchDocumentations(
  searchPhrase: string
): Promise<DocumentationModel[]> {
  await awaitDocumentationsDownload();
  const start = Date.now();
  const documentations = await getSearchedDocumentations(searchPhrase);
  debugLog('getSearchedDocumentations time:', Date.now() - start);
  return documentations;
}
